// Zakat worksheet — blank Word + CSV templates for the Amanah Estate
// Planning Kit. Mirrors the data the in-app Personal Zakat calculator
// collects so a user can fill it by hand, walk it into a masjid workshop
// or a scholar's office, and then key it into the calculator later.
//
// Structure roughly follows /app/frontend/src/config/zakatConfig.js:
//   1. Personal details + Hawl anniversary
//   2. Nisab basis choice + live-price snapshot slots
//   3. Zakatable assets (11 categories)
//   4. Immediate deductions
//   5. Net wealth calculation stubs
//   6. Zakat due + preferred disbursement categories (8 Qur'anic recipients)
//   7. Signature / witness lines

import { buildAmanahSection } from './amanahDocxTheme';

const stamp = () => new Date().toISOString().slice(0, 10);
const loadDocx = () => import('docx');

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * Blank Zakat worksheet — .docx
 */
export const downloadZakatWorksheetDocx = async (_state = null, { asBlob = false } = {}) => {
  const docx = await loadDocx();
  const {
    Document, Packer, Paragraph, TextRun, AlignmentType, UnderlineType,
    Table, TableRow, TableCell, WidthType, BorderStyle,
  } = docx;

  const BORDERS = {
    top:    { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    bottom: { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    left:   { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    right:  { style: BorderStyle.SINGLE, size: 4, color: 'AAAAAA' },
    insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'CCCCCC' },
    insideVertical:   { style: BorderStyle.SINGLE, size: 2, color: 'CCCCCC' },
  };

  const cell = (text, opts = {}) => new TableCell({
    width: { size: opts.w || 50, type: WidthType.PERCENTAGE },
    children: [new Paragraph({ children: [new TextRun({ text: String(text), bold: !!opts.bold, size: 22 })] })],
  });
  const blank = (w = 40) => new TextRun({
    text: '\u00A0'.repeat(w),
    underline: { type: UnderlineType.SINGLE, color: '888888' },
    size: 22,
  });
  const h = (t) => new Paragraph({ spacing: { before: 240, after: 120 },
    children: [new TextRun({ text: t, bold: true, size: 26, color: '0F4C3A' })] });
  const p = (t, opts = {}) => new Paragraph({ spacing: { after: 100 },
    children: [new TextRun({ text: t, size: 22, ...opts })] });
  const lbl = (label) => new Paragraph({
    spacing: { after: 160 },
    children: [new TextRun({ text: `${label}: `, bold: true, size: 22 }), blank(50)],
  });

  // Asset table with Value + Currency + Notes columns
  const twoColHeader = new TableRow({ children: [
    cell('Asset category', { bold: true, w: 55 }),
    cell('Value on Hawl day', { bold: true, w: 30 }),
    cell('Notes', { bold: true, w: 15 }),
  ]});
  const assetRows = [twoColHeader,
    ...[
      'Cash — on hand + wallets',
      'Bank balances (current + savings)',
      'Gold — weight × 24K spot price',
      'Silver — weight × spot price',
      'Business inventory (trade goods)',
      'Receivables — money owed TO you (recoverable)',
      'Stocks / mutual funds (trading basis)',
      'Cryptocurrency — market value',
      'Rental deposits held',
      'Investment savings certificates',
      'Livestock (camels 5+, cows 30+, sheep 40+)',
      'Agricultural produce at harvest (5 wasq / ~653 kg)',
    ].map((asset) => new TableRow({ children: [
      cell(asset, { w: 55 }),
      cell('', { w: 30 }),
      cell('', { w: 15 }),
    ]})),
  ];

  const deductionRows = [
    new TableRow({ children: [
      cell('Immediate liability', { bold: true, w: 55 }),
      cell('Amount', { bold: true, w: 30 }),
      cell('Notes', { bold: true, w: 15 }),
    ]}),
    ...[
      'Outstanding bills due this month',
      'Current-month rent / mortgage instalment',
      'Short-term debts due within the next lunar year',
      'Unpaid mahr (a debt on the husband)',
      'Business supplier payments due',
    ].map((row) => new TableRow({ children: [
      cell(row, { w: 55 }),
      cell('', { w: 30 }),
      cell('', { w: 15 }),
    ]})),
  ];

  const disbursementRows = [
    new TableRow({ children: [
      cell('Qur\u2019anic recipient (Q 9:60)', { bold: true, w: 55 }),
      cell('Allocation', { bold: true, w: 30 }),
      cell('Contact / notes', { bold: true, w: 15 }),
    ]}),
    ...[
      'Al-Fuqara (the poor)',
      'Al-Masakin (the needy)',
      'Al-\u2018Amilin (Zakat administrators)',
      'Al-Mu\u2019allafah (reconciliation of hearts)',
      'Fi al-Riqab (freeing captives)',
      'Al-Gharimin (debt-ridden)',
      'Fi Sabilillah (in the path of Allah)',
      'Ibn al-Sabil (stranded traveller)',
    ].map((row) => new TableRow({ children: [
      cell(row, { w: 55 }),
      cell('', { w: 30 }),
      cell('', { w: 15 }),
    ]})),
  ];

  const doc = new Document({ sections: [buildAmanahSection(docx, {
    docTitle: 'Amanah document',
    children: [
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 },
        children: [new TextRun({ text: '\u0628\u0633\u0645 \u0627\u0644\u0644\u0647 \u0627\u0644\u0631\u062D\u0645\u0646 \u0627\u0644\u0631\u062D\u064A\u0645', size: 28 })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 60 },
        children: [new TextRun({ text: 'Zakat Worksheet', bold: true, size: 36, color: '0F4C3A' })] }),
      new Paragraph({ alignment: AlignmentType.CENTER, spacing: { after: 300 },
        children: [new TextRun({ text: 'Personal · 2.5% of qualifying wealth held one lunar year',
          italics: true, size: 22, color: '666666' })] }),
      p('Fill this worksheet by hand, then key the numbers into the Personal Zakat Calculator at zakat.international. Amanah never uploads what you type.', { italics: true, color: '666666' }),

      h('1 \u00B7 Muzakki (Zakat payer)'),
      lbl('Full name'),
      lbl('Country / currency'),
      lbl('Hawl anniversary (Hijri date each year)'),
      lbl('Madhhab (Hanafi / Maliki / Shafi\u2019i / Hanbali / Ja\u2019fari)'),

      h('2 \u00B7 Nisab basis on Hawl day'),
      p('Choose whichever the majority of contemporary scholars recommend for you \u2014 usually the LOWER silver nisab so more Muslims become eligible.'),
      lbl('24K gold spot price today (per gram, local currency)'),
      lbl('Silver spot price today (per gram, local currency)'),
      lbl('Nisab chosen (gold 85 g \u2261  ~ ) OR (silver 595 g \u2261  ~ )'),

      h('3 \u00B7 Zakatable assets held one lunar year'),
      p('Skip assets used personally: primary home, primary vehicle, everyday clothing, tools of a non-trade job.'),
      new Table({ rows: assetRows, width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS }),
      new Paragraph({ spacing: { before: 200 },
        children: [new TextRun({ text: 'TOTAL ASSETS (A): ', bold: true, size: 22 }), blank(30)] }),

      h('4 \u00B7 Immediate liabilities'),
      new Table({ rows: deductionRows, width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS }),
      new Paragraph({ spacing: { before: 200 },
        children: [new TextRun({ text: 'TOTAL DEDUCTIONS (D): ', bold: true, size: 22 }), blank(30)] }),

      h('5 \u00B7 Net zakatable wealth'),
      new Paragraph({ spacing: { after: 80 },
        children: [new TextRun({ text: 'Net wealth (A - D): ', bold: true, size: 22 }), blank(30)] }),
      new Paragraph({ spacing: { after: 160 },
        children: [new TextRun({ text: 'Is Net wealth \u2265 Nisab?    \u25A2 Yes   \u25A2 No', size: 22 })] }),

      h('6 \u00B7 Zakat due (2.5% of net if above Nisab)'),
      new Paragraph({ spacing: { after: 200 },
        children: [new TextRun({ text: 'Zakat amount = 0.025 \u00D7 Net wealth = ', bold: true, size: 22 }), blank(30)] }),

      h('7 \u00B7 Preferred disbursement (Q 9:60)'),
      p('Allocate to any of the 8 Qur\u2019anic recipient categories. You may split across several.'),
      new Table({ rows: disbursementRows, width: { size: 100, type: WidthType.PERCENTAGE }, borders: BORDERS }),

      h('8 \u00B7 Signature'),
      new Paragraph({ spacing: { before: 300 },
        children: [new TextRun({ text: 'Muzakki signature: ', bold: true, size: 22 }), blank(50)] }),
      new Paragraph({ spacing: { before: 100 },
        children: [new TextRun({ text: 'Date: ', bold: true, size: 22 }), blank(30)] }),
      new Paragraph({ spacing: { before: 200 },
        children: [new TextRun({ text: 'This worksheet is a planning aid only. For a legally-binding Zakat declaration in jurisdictions that require it, please use your country\u2019s official Zakat authority form and file separately.', italics: true, size: 18, color: '666666' })] }),
    ],
  })] });

  const blob = await Packer.toBlob(doc);
  if (asBlob) return blob;
  downloadBlob(blob, `zakat-worksheet-${stamp()}.docx`);
  return null;
};

/**
 * Blank Zakat worksheet — .csv
 * A single-sheet spreadsheet that a user can open in Excel/Numbers/Sheets
 * and fill in digitally. Row structure mirrors the DOCX headings.
 */
export const downloadZakatWorksheetCsv = (_state = null, { asBlob = false } = {}) => {
  const rows = [
    ['Amanah Zakat Worksheet', '', '', 'Generated', stamp()],
    [],
    ['1. Muzakki'],
    ['Full name', ''],
    ['Country / currency', ''],
    ['Hawl anniversary (Hijri)', ''],
    ['Madhhab', ''],
    [],
    ['2. Nisab basis'],
    ['24K gold spot price / g', ''],
    ['Silver spot price / g', ''],
    ['Chosen Nisab (gold / silver)', ''],
    ['Nisab value (local currency)', ''],
    [],
    ['3. Zakatable assets — Value on Hawl day', 'Value', 'Notes'],
    ['Cash on hand + wallets', '', ''],
    ['Bank balances', '', ''],
    ['Gold (24K equivalent)', '', ''],
    ['Silver', '', ''],
    ['Business inventory', '', ''],
    ['Receivables owed to you', '', ''],
    ['Stocks / mutual funds (trading basis)', '', ''],
    ['Cryptocurrency', '', ''],
    ['Rental deposits held', '', ''],
    ['Investment savings certificates', '', ''],
    ['Livestock', '', ''],
    ['Agricultural produce at harvest', '', ''],
    ['TOTAL ASSETS (A)', '=SUM(B16:B27)'],
    [],
    ['4. Immediate liabilities', 'Amount', 'Notes'],
    ['Outstanding bills due', '', ''],
    ['Current-month rent / mortgage', '', ''],
    ['Short-term debts due', '', ''],
    ['Unpaid mahr', '', ''],
    ['Supplier payments due', '', ''],
    ['TOTAL DEDUCTIONS (D)', '=SUM(B31:B35)'],
    [],
    ['5. Net zakatable wealth'],
    ['Net wealth (A - D)', '=B28-B36'],
    ['Above Nisab?', ''],
    [],
    ['6. Zakat due'],
    ['Zakat amount (2.5% of net)', '=IF(B40>0,B40*0.025,0)'],
    [],
    ['7. Disbursement (Q 9:60)', 'Allocation', 'Contact / notes'],
    ['Al-Fuqara (the poor)', '', ''],
    ['Al-Masakin (the needy)', '', ''],
    ["Al-'Amilin (Zakat administrators)", '', ''],
    ["Al-Mu'allafah (reconciliation)", '', ''],
    ['Fi al-Riqab (freeing captives)', '', ''],
    ['Al-Gharimin (debt-ridden)', '', ''],
    ['Fi Sabilillah (in the path of Allah)', '', ''],
    ['Ibn al-Sabil (stranded traveller)', '', ''],
  ];

  const esc = (v) => {
    const s = String(v ?? '');
    return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = rows.map((r) => r.map(esc).join(',')).join('\n') + '\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  if (asBlob) return blob;
  downloadBlob(blob, `zakat-worksheet-${stamp()}.csv`);
  return null;
};
